// ── 연차정산 처리 모달 ────────────────────────────────────────────────────────
//   정산일: ① 자동 계산값(기준일 지난 뒤 오는 급여일 + 휴일 규칙) ② 다른 날짜
//     달력은 연차 추가 모달과 동일한 공용 MultiDatePicker(single 모드)를 **항상 펼친 상태**로 표시하고,
//     달력에서 날짜를 클릭하면 자동으로 ②로 전환된다.
//   통상일급: 자동계산값 표시. 자동계산이 안 되면(출퇴근 템플릿 없음 등) 직접 입력.
import React, { useMemo, useState } from 'react';
import Modal from '../../../components/Modal';
import MultiDatePicker from '../../../components/MultiDatePicker';

const won = (n) => `${Math.round(Number(n) || 0).toLocaleString('ko-KR')}원`;
const floor10 = (n) => Math.floor((Number(n) || 0) / 10) * 10;

export default function SettleModal({ target, onClose, onSubmit, saving }) {
  const autoDate = target?.next_settlement_date || null;
  const autoWage = Number(target?.daily_wage) || 0;
  const needsManualWage = !(autoWage > 0);

  // 정산일 선택 모드: 'auto' = 자동 계산값 / 'custom' = 달력에서 직접 선택
  const [dateMode, setDateMode] = useState(autoDate ? 'auto' : 'custom');
  const [customDate, setCustomDate] = useState(autoDate || '');
  const [days, setDays] = useState(target ? String(target.remaining) : '');
  const [manualWage, setManualWage] = useState('');
  const [reason, setReason] = useState('');

  const settleDate = dateMode === 'auto' ? autoDate : customDate;
  const dailyWage = needsManualWage ? (Number(manualWage.replace(/,/g, '')) || 0) : autoWage;
  const amount = useMemo(() => {
    const d = Number(days);
    return d > 0 && dailyWage > 0 ? floor10(d * dailyWage) : 0;
  }, [days, dailyWage]);

  if (!target) return null;

  function submit() {
    onSubmit({
      days: Number(days),
      date: settleDate,
      reason: reason.trim(),
      dailyWage,
      amount,
      manual: needsManualWage,
    });
  }

  return (
    <Modal open={!!target} title="연차정산 처리" icon="💰" onClose={onClose}
      width="min(680px, 96vw)"
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" style={ghostBtn} onClick={onClose}>취소</button>
          <button type="button" style={primaryBtn} onClick={submit} disabled={saving}>{saving ? '처리 중…' : '정산 처리'}</button>
        </div>
      )}>
      <div style={{ display: 'grid', gap: 12 }}>
        {/* 대상 요약 */}
        <div style={{ fontSize: 13 }}>
          <strong>{target.name}</strong>
          {target.settlement_type === 'resign' && (
            <span style={resignBadge}>퇴사 {String(target.resign_date).slice(0, 10)}</span>
          )}
          <span> · 미사용 {Number(target.remaining).toLocaleString('ko-KR')}일</span>
          {autoWage > 0 && <span> · 통상일급 {won(autoWage)}</span>}
        </div>

        {/* 정산일 — 자동값 / 다른 날짜. 달력은 연차 추가와 동일하게 항상 펼쳐진 상태. */}
        <div style={{ display: 'grid', gap: 6 }}>
          <span style={labelStyle}>정산일</span>
          {autoDate && (
            <label style={radioRow(dateMode === 'auto')}>
              <input type="radio" checked={dateMode === 'auto'} onChange={() => setDateMode('auto')} />
              <span style={{ fontWeight: 800, fontSize: 13 }}>{autoDate}</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                기준일 지난 뒤 오는 급여일 + 휴일 규칙
                {target.settlement_base ? ` (기준일 ${target.settlement_base})` : ''}
              </span>
            </label>
          )}
          <label style={radioRow(dateMode === 'custom')}>
            <input type="radio" checked={dateMode === 'custom'} onChange={() => setDateMode('custom')} />
            <span style={{ fontWeight: 700, fontSize: 13 }}>다른 날짜</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>아래 달력에서 날짜를 클릭하세요</span>
            {dateMode === 'custom' && customDate && (
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{customDate}</span>
            )}
          </label>
          {/* 연차 추가 모달과 같은 달력(MultiDatePicker) — 항상 표시. 클릭 시 '다른 날짜'로 자동 전환. */}
          <MultiDatePicker
            single
            singleLabel="선택한 정산일"
            singleHint="날짜를 클릭해 정산일을 선택하세요"
            selectedDates={settleDate ? [settleDate] : []}
            onChange={(dates) => {
              const picked = dates[0] || '';
              if (!picked) return;
              setCustomDate(picked);
              setDateMode(picked === autoDate ? 'auto' : 'custom');
            }}
          />
        </div>

        {/* 정산 일수 */}
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={labelStyle}>정산 일수 (기본 = 미사용 전량)</span>
          <input type="text" inputMode="decimal" value={days} style={inputStyle}
            onChange={(e) => setDays(e.target.value.replace(/[^0-9.]/g, ''))} />
        </label>

        {/* 통상일급 — 자동계산 불가 시 직접 입력 */}
        {needsManualWage && (
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={labelStyle}>
              통상일급 직접 입력
              <span style={{ color: '#dc2626', fontWeight: 600 }}> · {target.wage_note || '급여 데이터 없음'}으로 자동계산 불가</span>
            </span>
            <input type="text" inputMode="numeric" value={manualWage} placeholder="예: 84000" style={{ ...inputStyle, textAlign: 'right' }}
              onChange={(e) => setManualWage(e.target.value.replace(/[^0-9]/g, ''))} />
          </label>
        )}

        {/* 사유 */}
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={labelStyle}>사유 (선택 — 미입력 시 자동 기록)</span>
          <input type="text" value={reason} placeholder="예: 퇴사 정산 / 2026년 미사용 연차 정산" style={inputStyle}
            onChange={(e) => setReason(e.target.value)} />
        </label>

        {/* 정산액 미리보기 */}
        <div style={amountBox}>
          정산액: <span style={{ color: 'var(--accent-700, #7c3aed)' }}>{won(amount)}</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>
            {' '}({Number(days) || 0}일 × {won(dailyWage)})
          </span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          정산 처리 시 연차 잔여가 차감되고 로그에 기록됩니다. 급여에는 영향을 주지 않으며, 정산액은 이 시점 금액으로 고정 저장됩니다.
        </div>
      </div>
    </Modal>
  );
}

const labelStyle = { fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' };
const inputStyle = { height: 32, padding: '0 8px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text)', width: '100%', boxSizing: 'border-box' };
const radioRow = (on) => ({
  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', cursor: 'pointer',
  padding: '8px 10px', borderRadius: 8,
  border: `1px solid ${on ? 'var(--accent-700, #7c3aed)' : 'var(--border)'}`,
  background: on ? 'rgba(124,58,237,0.06)' : 'var(--bg-card)',
});
const amountBox = { background: 'var(--bg-subtle)', borderRadius: 8, padding: 12, fontSize: 14, fontWeight: 800 };
const primaryBtn = { height: 32, padding: '0 14px', border: 'none', borderRadius: 6, background: 'var(--accent-700, #7c3aed)', color: '#fff', fontWeight: 800, cursor: 'pointer' };
const ghostBtn = { height: 32, padding: '0 12px', border: '1px solid var(--border)', borderRadius: 6, background: 'transparent', color: 'var(--text-muted)', fontWeight: 700, cursor: 'pointer' };
const resignBadge = { marginLeft: 6, fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999, background: 'rgba(100,116,139,0.15)', color: '#475569', border: '1px solid rgba(100,116,139,0.3)' };
